import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const { enabled, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || '/';

  const [mode, setMode] = useState<'signin' | 'bootstrap'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!enabled) {
    navigate('/settings#xero', { replace: true });
    return null;
  }

  const switchMode = (next: 'signin' | 'bootstrap') => {
    setMode(next);
    setError(null);
    setPassword('');
    setConfirmPassword('');
  };

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);

    if (mode === 'bootstrap') {
      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    setBusy(true);
    try {
      if (mode === 'bootstrap') {
        await register(email, password);
      } else {
        await login(email, password);
      }
      navigate(from, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : mode === 'bootstrap' ? 'Registration failed' : 'Sign in failed';
      // After the first user exists, register is invite-only — surface that clearly.
      if (mode === 'bootstrap' && /invite-only|no users exist|Bootstrap registration/i.test(message)) {
        setError('Registration is invite-only. The first admin account already exists — please sign in.');
        setMode('signin');
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const isBootstrap = mode === 'bootstrap';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#0a0e17',
        px: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <HubOutlinedIcon color="primary" />
            <Typography variant="h5" fontWeight={800}>
              Growth Hub
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isBootstrap
              ? 'Create the first admin account for this Growth Hub instance.'
              : 'Sign in with the email and password your administrator provided.'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={(e) => void submit(e)}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                required
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                autoComplete={isBootstrap ? 'new-password' : 'current-password'}
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                required
                helperText={isBootstrap ? 'At least 8 characters' : undefined}
              />
              {isBootstrap && (
                <TextField
                  label="Confirm password"
                  type="password"
                  fullWidth
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(ev) => setConfirmPassword(ev.target.value)}
                  required
                />
              )}
              <Button type="submit" variant="contained" size="large" disabled={busy}>
                {isBootstrap ? 'Create admin account' : 'Sign in'}
              </Button>
            </Stack>
          </Box>

          <Stack spacing={1} sx={{ mt: 2.5 }}>
            {isBootstrap ? (
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => switchMode('signin')}
                  sx={{ verticalAlign: 'baseline' }}
                >
                  Sign in
                </Link>
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                First time setup?{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => switchMode('bootstrap')}
                  sx={{ verticalAlign: 'baseline' }}
                >
                  Create first admin account
                </Link>
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {isBootstrap
                  ? 'Only works when the database has no users yet. After that, access is invite-only.'
                  : 'Access is invite-only. Contact an admin if you need an account.'}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
